import type { LocalDate, Patient } from '@ergo/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { placesOf } from '../appointments';
import { useResource } from '../hooks';
import { formatEurosShort } from '../money';
import { daysBetween, formatShortDate, today } from '../time';

/**
 * L'écran Patients (docs/redesign-plus-douce.md, §Écran Patients) : une liste de
 * familles, pas un tableau de soldes. Ce que la carte dit, c'est un mot : « à relancer »,
 * « en avance », « à jour » ; le compte lui-même se lit et se règle sur la fiche.
 */
export function Patients() {
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');

  const patients = useResource(
    () => api.patients.list(showInactive ? undefined : { active: true }),
    [showInactive],
  );

  const visible = useMemo(() => {
    const needle = normalize(search.trim());
    if (!needle) return patients.data ?? [];
    return (patients.data ?? []).filter((patient) => normalize(patient.name).includes(needle));
  }, [patients.data, search]);

  // La pilule compte sur toute la liste chargée, pas sur ce que la recherche laisse
  // voir : chercher un nom ne solde aucun compte.
  const debtors = (patients.data ?? []).filter((patient) => patient.soldeEur < 0).length;
  const day = today();

  return (
    <section className="page patients">
      <header className="patients__head">
        <h1 className="patients__title">Patients</h1>
        <Link className="patients__new" to="/patients/nouveau">
          + Nouveau
        </Link>
      </header>

      {patients.data && (
        <p className={`pilule pilule--etat pilule--${debtors > 0 ? 'warm' : 'ok'}`}>
          {debtors > 0 ? (
            <>
              <span className="pilule__dot" aria-hidden="true" />
              {debtors === 1 ? '1 patient à relancer' : `${debtors} patients à relancer`}
            </>
          ) : (
            <>
              <span aria-hidden="true">✓</span>
              Tous les comptes sont à jour
            </>
          )}
        </p>
      )}

      <input
        className="patients__search"
        type="search"
        placeholder="Rechercher un nom…"
        aria-label="Rechercher un patient"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <label className="checkbox patients__inactive">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(event) => setShowInactive(event.target.checked)}
        />
        Afficher les patients inactifs
      </label>

      {patients.error && (
        <p className="error" role="alert">
          {patients.error}
        </p>
      )}
      {patients.loading && !patients.data && <p className="muted">Chargement…</p>}

      {patients.data && visible.length === 0 && (
        <p className="empty">
          {search.trim()
            ? 'Aucun patient ne correspond à cette recherche.'
            : 'Aucun patient pour le moment.'}
        </p>
      )}

      <ul className="patients__list">
        {visible.map((patient) => {
          const tone = toneOf(patient.soldeEur);
          const relance = relanceLine(patient, day);

          return (
            <li className="patients__item" key={patient.id}>
              <div className="patients__row">
                <span className="patients__label">
                  <span className="patients__name">
                    {/* La carte entière ouvre la fiche : le lien porte le nom et
                        s'étire sur la ligne. */}
                    <Link className="patients__link" to={`/patients/${patient.id}`}>
                      {patient.name}
                    </Link>
                    {patient.soldeEur < 0 && <span className="patients__dot" aria-hidden="true" />}
                    {patient.cabinet && <span className="badge">Cabinet</span>}
                    {!patient.active && <span className="badge badge--muted">Inactif</span>}
                  </span>
                  <span className="patients__meta">{metaOf(patient)}</span>
                  {relance && <span className="patients__since">{relance}</span>}
                </span>

                <span className={`patients__state patients__state--${tone}`}>
                  {STATE_WORDS[tone]}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

type Tone = 'warm' | 'ok' | 'zero';

const STATE_WORDS: Record<Tone, string> = {
  warm: 'à relancer',
  ok: 'en avance',
  zero: 'à jour',
};

function toneOf(soldeEur: number): Tone {
  if (soldeEur < 0) return 'warm';
  return soldeEur > 0 ? 'ok' : 'zero';
}

/**
 * Une facture met un mois à être réglée, et souvent en plusieurs fois : avant ce
 * délai, la rappeler serait presser une famille qui n'est pas en retard.
 */
const RELANCE_AFTER_DAYS = 30;

/**
 * La ligne douce : « Facture du 04/08 · 112 € reçus depuis, il en manque un peu ». Elle
 * ne dit ni le total dû ni le retard en jours — juste de quand date la facture et ce qui
 * est arrivé depuis, pour que la relance se prépare sans que personne soit accusé.
 */
function relanceLine(patient: Patient, day: LocalDate): string | null {
  const relance = patient.relance;
  if (patient.soldeEur >= 0 || relance === null) return null;
  if (daysBetween(relance.lastNoteOn, day) < RELANCE_AFTER_DAYS) return null;

  const since =
    relance.receivedSinceEur > 0
      ? `${formatEurosShort(relance.receivedSinceEur)} reçus depuis, il en manque un peu`
      : 'rien reçu pour l’instant';
  return `Facture du ${formatShortDate(relance.lastNoteOn)} · ${since}`;
}

/** Où on la voit et comment on la joint : « Domicile · 06 12 34 56 78 ». */
function metaOf(patient: Patient): string {
  const parts = placesOf(patient).map((location) => location.label);
  if (patient.contact) parts.push(patient.contact);
  return parts.length === 0 ? 'Aucun lieu enregistré' : parts.join(' · ');
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
